import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { Formik } from "formik";
import * as yup from "yup";
import api from 'state/axios';
import {
    useTheme,
    useMediaQuery,
    Box,
    Typography,
    CircularProgress,
    TextField,
    Button,
    MenuItem,
    FormControlLabel, 
    Checkbox, 
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { de, enGB } from 'date-fns/locale';
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import useAuthCheck from "hooks/useAuthCheck";
import { LAYOUT_HEIGHT } from "state/constants";
import { parseUTCDate, capitalizeFirstLetter } from 'state/helpers';
import FlexBetween from "components/FlexBetween";
import FlexCenter from "components/FlexCenter";
import Header from "components/Header";
import StyledButton from 'components/StyledButton';
import CustomDatePicker from "components/CustomDatePicker";
import ApplicableTo from "./ApplicableTo";


const CostItemPage = () => {
    useAuthCheck();
    const navigate = useNavigate();
    const locales = { 'en-us': undefined, 'en-gb': enGB, de };
    const { costItemId } = useParams();
    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const [jobTitles, setJobTitles] = useState([]);
    const [formValues, setFormValues] = useState({
        costItemCode: "",
        costItemName: "",
        costGroup: "allowance",
        category: "variable",
        frequency: "monthly",
        formula: "",
        effectiveFrom: new Date(),
        effectiveTo: null,
        description: "",
        excludeIfResigned: false,
        isContributoryIncome: false,
        isNonTaxableIncome: false,
        sortOrder: 0,
        isActive: true,
        applicableTo: [],
    });


    const fetchJobTitles = useCallback(async () => {
        return await api.get('/jobtitles/active');
    }, []);
    
    const fetchCostItem = useCallback(async () => {
        if (costItemId) {
            const url = `/costitems/${costItemId}`;
            return await api.get(url);            
        }
    }, [costItemId]);

    useEffect(() => {
        const fetchData = async () => {
            try {
                setError(null);
                setLoading(true);
                
                const [jobTitlesResponse,
                    costItemResponse
                ] = await Promise.all([
                    fetchJobTitles(),
                    costItemId ? fetchCostItem() : Promise.resolve(null),
                ]);
                
                setJobTitles(jobTitlesResponse.data);        
                
                const formData = costItemResponse?.data || {};             
                setFormValues({
                    costItemCode: formData.costItemCode || "",
                    costItemName: formData.costItemName || "",
                    costGroup: formData.costGroup || "",
                    category: formData.category || "variable",
                    frequency: formData.frequency || "oneTime",
                    formula: formData.formula || "",
                    effectiveFrom: formData.effectiveFrom ? new Date(formData.effectiveFrom) : null,
                    effectiveTo: formData.effectiveTo ? new Date(formData.effectiveTo) : null,
                    description: formData.description || "",
                    excludeIfResigned: formData.excludeIfResigned ?? false,
                    isContributoryIncome: formData.isContributoryIncome ?? false,
                    isNonTaxableIncome: formData.isNonTaxableIncome ?? false,
                    sortOrder: formData.sortOrder || 0,
                    isActive: formData.isActive ?? true,
                    applicableTo: formData.applicableTo || [],
                });                
            } catch (error) {
                setError(error);
                toast.error(error.response?.data?.message || "Failed to fetch data!");
            } finally {
                setLoading(false);
            }
        };
    
        fetchData();      
    }, [
        fetchJobTitles, 
        fetchCostItem, 
        costItemId,
    ]); 

    const checkoutSchema = yup.object().shape({
        costItemCode: yup.string().required("Required"),
        costItemName: yup.string().required("Required"),
        costGroup: yup.string().required("Required"),
        category: yup.string().required("Required"),
    });

    const handleFormSubmit = async (values) => {
        try {
            const dateFields = [                
                'effectiveFrom',
                'effectiveTo',
            ];

            const payload = {
                ...values,
                ...dateFields.reduce((acc, field) => {
                    acc[field] = values[field] ? parseUTCDate(values[field]) : null;
                    return acc;
                }, {}),
            };
            
            const response = await api({
                method: costItemId ? "PUT" : "POST",
                url: costItemId
                    ? `/costitems/update/${costItemId}`
                    : '/costitems/new',
                data: payload,
            });

            if (![200, 201].includes(response.status)) {
                toast.error("Failed to submit the form!");
                return;
            }

            toast.success(
                costItemId
                    ? "Cost item updated successfully."
                    : "Cost item created successfully."
            );

            navigate("/costitems");
        } catch (error) {
            setError(error);
            toast.error(error.message || "Failed to submit the form!");
        }
    };

    return (
        <Box className="main-container-box">
            <FlexBetween>
                <Header
                    title="Cost Item"
                    subtitle={`${loading ? "Loading..."
                        : costItemId
                            ? "Update"
                            : "Create"
                    }`}
                />
                {!loading && (
                    <Box display="flex" alignItems="center" gap={1}>
                        <StyledButton 
                            onClick={() => navigate("/costitems")}
                        >
                            <ArrowBackIosNewIcon />
                        </StyledButton>
                    </Box>
                )}
            </FlexBetween>
            {loading ? (
                <FlexCenter height={LAYOUT_HEIGHT}>
                    <CircularProgress />
                </FlexCenter>
            ) : error ? (
                <Typography color="error">Error: {error?.message || ''}</Typography>
            ) : (
                <Box height={LAYOUT_HEIGHT} sx={{ overflowY: "auto" }}>
                    <LocalizationProvider 
                        dateAdapter={AdapterDateFns}
                        adapterLocale={locales['en-gb']}
                    >
                        <Formik
                            initialValues={formValues}
                            enableReinitialize={true}
                            validationSchema={checkoutSchema}
                            onSubmit={handleFormSubmit}
                        >
                            {({
                                values,
                                errors,
                                touched,
                                handleBlur,
                                handleChange,
                                handleSubmit,
                                setFieldValue,
                            }) => (
                                <form onSubmit={handleSubmit}>
                                    <Box
                                        display="grid"
                                        gridTemplateColumns="repeat(12, minmax(0, 1fr))"
                                        gap={2}
                                        sx={{ "& > div": { gridColumn: isDesktop ? undefined : "span 12" } }}
                                    >
                                        <TextField
                                            label="Cost Item Code"
                                            name="costItemCode"
                                            fullWidth
                                            variant="filled"
                                            type="text"
                                            onBlur={handleBlur}
                                            onChange={handleChange}
                                            value={values.costItemCode}
                                            error={
                                                !!touched.costItemCode &&
                                                !!errors.costItemCode
                                            }
                                            helperText={
                                                touched.costItemCode &&
                                                errors.costItemCode
                                            }
                                            sx={{ gridColumn: "span 6" }}
                                        />

                                        <TextField
                                            label="Cost Item Name"
                                            name="costItemName"
                                            fullWidth
                                            variant="filled"
                                            onBlur={handleBlur}
                                            onChange={handleChange}
                                            value={values.costItemName}
                                            error={
                                                !!touched.costItemName &&
                                                !!errors.costItemName
                                            }
                                            helperText={
                                                touched.costItemName &&
                                                errors.costItemName
                                            }
                                            sx={{ gridColumn: "span 6" }}
                                        />

                                        <CustomDatePicker
                                            label="Effective From"
                                            name="effectiveFrom"
                                            value={values.effectiveFrom}
                                            maxDate={values.effectiveTo}
                                            onChange={setFieldValue}
                                            touched={touched}
                                            errors={errors}
                                            sx={{ gridColumn: "span 6" }}
                                        />

                                        <CustomDatePicker
                                            label="Effective To"
                                            name="effectiveTo"
                                            value={values.effectiveTo}
                                            minDate={values.effectiveFrom}
                                            onChange={setFieldValue}
                                            touched={touched}
                                            errors={errors}
                                            sx={{ gridColumn: "span 6" }}
                                        />

                                        <TextField
                                            label="Cost Group"
                                            name="costGroup"
                                            select
                                            fullWidth
                                            variant="filled"
                                            onBlur={handleBlur}
                                            onChange={handleChange}
                                            value={values.costGroup}
                                            error={
                                                !!touched.costGroup &&
                                                !!errors.costGroup
                                            }
                                            helperText={
                                                touched.costGroup &&
                                                errors.costGroup
                                            }
                                            sx={{ gridColumn: "span 6" }}
                                        >
                                            {["salary","bonus","allowance","welfare","training","travel","hotac","admin","opex","capex","deduction","other"].map(
                                                (option) => (
                                                    <MenuItem key={option} value={option}>
                                                        {capitalizeFirstLetter(option)}
                                                    </MenuItem>
                                                )
                                            )}
                                        </TextField>

                                        <TextField
                                            label="Category"
                                            name="category"
                                            select
                                            fullWidth
                                            variant="filled"
                                            onBlur={handleBlur}
                                            onChange={handleChange}
                                            value={values.category}
                                            error={
                                                !!touched.category &&
                                                !!errors.category
                                            }
                                            helperText={
                                                touched.category && errors.category
                                            }
                                            sx={{ gridColumn: "span 6" }}
                                        >
                                            <MenuItem value="variable">Variable</MenuItem>
                                            <MenuItem value="fixed">Fixed</MenuItem>
                                        </TextField>

                                        <TextField
                                            label="Frequency"
                                            name="frequency"
                                            select
                                            fullWidth
                                            variant="filled"
                                            value={values.frequency}
                                            onBlur={handleBlur}
                                            onChange={handleChange}
                                            sx={{ gridColumn: "span 6" }}
                                        >
                                            {[
                                                { value: "monthly", label: "Monthly" },
                                                { value: "quarterly", label: "Quarterly" },
                                                { value: "yearly", label: "Yearly" },
                                                { value: "perDay", label: "Per Day" },
                                                { value: "perHour", label: "Per Hour" },
                                                { value: "perFlight", label: "Per Flight" },
                                                { value: "perDuty", label: "Per Duty" },
                                                { value: "oneTime", label: "One Time" },
                                            ].map((option) => (
                                                <MenuItem key={option.value} value={option.value}>
                                                    {option.label}
                                                </MenuItem>
                                            ))}
                                        </TextField>

                                        <TextField
                                            label="Formula"
                                            name="formula"
                                            fullWidth
                                            variant="filled"
                                            type="text"
                                            onBlur={handleBlur}
                                            onChange={handleChange}
                                            value={values.formula}
                                            error={
                                                !!touched.formula &&
                                                !!errors.formula
                                            }
                                            helperText={
                                                touched.formula &&
                                                errors.formula
                                            }
                                            sx={{ gridColumn: "span 6" }}
                                        />

                                        <TextField
                                            label="Description"
                                            name="description"
                                            fullWidth
                                            variant="filled"
                                            type="text"
                                            onBlur={handleBlur}
                                            onChange={handleChange}
                                            value={values.description}
                                            error={
                                                !!touched.description &&
                                                !!errors.description
                                            }
                                            helperText={
                                                touched.description &&
                                                errors.description
                                            }
                                            sx={{ gridColumn: "span 6" }}
                                        />

                                        <TextField
                                            label="Sort Order"
                                            name="sortOrder"
                                            type="number"
                                            fullWidth
                                            variant="filled"
                                            onBlur={handleBlur}
                                            onChange={handleChange}
                                            value={values.sortOrder}                                            
                                            slotProps={{
                                                input: {
                                                    sx: {
                                                        "& input": { textAlign: "right" },
                                                    },
                                                },
                                            }}
                                            sx={{ gridColumn: "span 6" }}
                                        />

                                        <FormControlLabel
                                            sx={{ gridColumn: isDesktop ? 'span 3' : 'span 6' }}
                                            control={
                                                <Checkbox
                                                    color="primary"
                                                    name="excludeIfResigned"
                                                    checked={values.excludeIfResigned}
                                                    onChange={handleChange}
                                                />
                                            }
                                            label="Exclude If Resigned"
                                        />
                                        <FormControlLabel
                                            sx={{ gridColumn: isDesktop ? 'span 3' : 'span 6' }}
                                            control={
                                                <Checkbox
                                                    color="primary"
                                                    name="isContributoryIncome"
                                                    checked={values.isContributoryIncome}
                                                    onChange={handleChange}
                                                />
                                            }
                                            label="Is Contributory Income"
                                        />
                                        <FormControlLabel
                                            sx={{ gridColumn: isDesktop ? 'span 3' : 'span 6' }}
                                            control={
                                                <Checkbox
                                                    color="primary"
                                                    name="isNonTaxableIncome"
                                                    checked={values.isNonTaxableIncome}
                                                    onChange={handleChange}
                                                />
                                            }
                                            label="Is Non-Taxable Income"
                                        />
                                        <FormControlLabel
                                            sx={{ gridColumn: isDesktop ? 'span 3' : 'span 6' }}
                                            control={
                                                <Checkbox
                                                    color="primary"
                                                    name="isActive"
                                                    checked={values.isActive}
                                                    onChange={handleChange}
                                                />
                                            }
                                            label="Is Active"
                                        />

                                        <Typography variant="h6" fontWeight="bold" sx={{ gridColumn: 'span 12' }}>
                                            Applicable To
                                        </Typography>                                        
                                        <Box sx={{ gridColumn: 'span 12', ml: 2 }}>
                                            <ApplicableTo
                                                values={values}
                                                setFieldValue={setFieldValue}
                                                jobTitles={jobTitles}
                                            />
                                        </Box>
                                                                                
                                        <Button
                                            fullWidth
                                            type="submit"
                                            variant="contained"
                                            color="secondary"
                                            sx={{ gridColumn: "span 12", height: 42 }}
                                        >
                                            {costItemId ? "Update" : "Create"}
                                        </Button>
                                    </Box>
                                </form>
                            )}
                        </Formik>
                    </LocalizationProvider>
                </Box>
            )}
        </Box>
    );
};

export default CostItemPage;